import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Typography,
  Stack,
  Box,
  CircularProgress,
} from '@mui/material';
import { Location } from '../types';
import { isValidUsStateAbbrev, normalizeUsState } from '../utils/usStates';
import AddressFields, { AddressValue } from './AddressFields';

export interface TransportationLocationDraft {
  location_address_line_1: string;
  location_address_line_2: string;
  location_city: string;
  location_state: string;
  location_zip: string;
  location_country: string;
  location_latitude: number | undefined;
  location_longitude: number | undefined;
}

export interface TransportationLocationClientHome {
  client_id: number | string;
  first_name: string;
  last_name: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
}

type Suggestion = Location & { _section: string };

export function transportationFormatLocationLine(location: Location | undefined): string {
  if (!location) return '';
  const parts = [
    location.location_address_line_1,
    location.location_city,
    location.location_state,
    location.location_zip,
  ].filter(Boolean);
  return parts.join(', ').toUpperCase();
}

function toUpperAddress(value: string): string {
  return value.toUpperCase();
}

function locationSearchBlob(loc: Location): string {
  return [
    loc.location_address_line_1,
    loc.location_address_line_2,
    loc.location_city,
    loc.location_state,
    loc.location_zip,
    loc.location_country,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/** Collapse rows that differ only by location_id (same street/city/state/zip). */
function locationDedupeKey(loc: Location): string {
  const norm = (s: string | undefined) =>
    (s ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  const zip = norm(loc.location_zip).replace(/[^a-z0-9]/g, '');
  return [norm(loc.location_address_line_1), norm(loc.location_address_line_2), norm(loc.location_city), norm(loc.location_state), zip].join(
    '\u0001'
  );
}

function hasUsableAddressKey(loc: Location): boolean {
  return Boolean(
    (loc.location_address_line_1 || '').trim() &&
      (loc.location_city || '').trim() &&
      (loc.location_state || '').trim() &&
      (loc.location_zip || '').trim()
  );
}

export interface TransportationLocationDialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  draft: TransportationLocationDraft;
  onDraftChange: React.Dispatch<React.SetStateAction<TransportationLocationDraft>>;
  providerId: number | null | undefined;
  /** @deprecated Unused — kept for call-site compatibility */
  clients?: TransportationLocationClientHome[];
  /** @deprecated Unused — kept for call-site compatibility */
  siblingStops?: { label: string; location: Location }[];
  onLocationResolved: (location: Location) => void;
  createLocation: (body: Record<string, unknown>) => Promise<Location>;
  /** Locations previously used in this provider's transportation */
  fetchProviderHistory: (providerId: number) => Promise<Location[]>;
  accentColor?: string;
  reportError?: (message: string) => void;
}

const LINE_BG_DEFAULT = '#2D4B73';

const TransportationLocationDialog: React.FC<TransportationLocationDialogProps> = ({
  open,
  onClose,
  title = 'Location',
  draft,
  onDraftChange,
  providerId,
  onLocationResolved,
  createLocation,
  fetchProviderHistory,
  accentColor = LINE_BG_DEFAULT,
  reportError,
}) => {
  const [loadingOpts, setLoadingOpts] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setShowErrors(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingOpts(true);
      try {
        const pid = providerId ? Number(providerId) : NaN;
        if (Number.isNaN(pid) || pid <= 0) {
          if (!cancelled) setSuggestions([]);
          return;
        }
        const hist = await fetchProviderHistory(pid).catch(() => [] as Location[]);
        const histArr = Array.isArray(hist) ? hist : [];
        const seenIds = new Set<string>();
        const seenAddressKeys = new Set<string>();
        const merged = histArr
          .map(h => ({ ...h, _section: 'Previously used by this provider' }))
          .filter(s => {
            const idStr = s.location_id != null ? String(s.location_id) : '';
            if (!idStr || seenIds.has(idStr)) return false;
            if (hasUsableAddressKey(s)) {
              const addrKey = locationDedupeKey(s);
              if (seenAddressKeys.has(addrKey)) return false;
              seenAddressKeys.add(addrKey);
            }
            seenIds.add(idStr);
            return true;
          });
        if (!cancelled) setSuggestions(merged);
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoadingOpts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, providerId, fetchProviderHistory]);

  const handlePickSaved = (_: React.SyntheticEvent, value: Suggestion | null) => {
    if (!value) return;
    const { _section, ...loc } = value;
    void _section;
    onLocationResolved(loc as Location);
  };

  const handleSaveNew = async () => {
    if (savingRef.current) return;
    const stateAbbrev = normalizeUsState(draft.location_state);
    if (
      !draft.location_address_line_1.trim() ||
      !draft.location_city.trim() ||
      !stateAbbrev ||
      !draft.location_zip.trim()
    ) {
      setShowErrors(true);
      reportError?.('Please fill in Address, City, State, and Zip.');
      return;
    }
    if (!isValidUsStateAbbrev(stateAbbrev)) {
      setShowErrors(true);
      reportError?.('State must be a 2-letter US abbreviation (e.g. OH, NV).');
      return;
    }
    setShowErrors(false);
    savingRef.current = true;
    try {
      setSaving(true);
      const newLoc = await createLocation({
        location_address_line_1: toUpperAddress(draft.location_address_line_1.trim()),
        location_address_line_2: toUpperAddress(draft.location_address_line_2.trim() || ''),
        location_city: toUpperAddress(draft.location_city.trim()),
        location_state: stateAbbrev,
        location_zip: toUpperAddress(draft.location_zip.trim()),
        location_country: toUpperAddress(draft.location_country.trim() || 'US'),
        location_latitude: draft.location_latitude ?? null,
        location_longitude: draft.location_longitude ?? null,
      });
      if (!newLoc?.location_id) {
        reportError?.('Location was created but no id was returned.');
        return;
      }
      onLocationResolved(newLoc);
    } catch (err: unknown) {
      const ex = err as { message?: string; response?: { data?: { detail?: string } } };
      reportError?.(ex?.response?.data?.detail || ex?.message || 'Failed to create location.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // Nested under Add Transportation — allow focus without raising z-index
      // (raising z-index puts Select/Autocomplete menus behind this dialog)
      disableEnforceFocus
      disableAutoFocus
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: '80vh', overflow: 'auto' }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Search previous locations, or enter a new address below.
            </Typography>
            <Autocomplete<Suggestion, false, false, false>
              options={suggestions}
              loading={loadingOpts}
              groupBy={o => o._section}
              getOptionLabel={o => transportationFormatLocationLine(o)}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return options.slice(0, 200);
                return options.filter(o => locationSearchBlob(o).includes(q)).slice(0, 200);
              }}
              isOptionEqualToValue={(a, b) => Number(a.location_id) === Number(b.location_id)}
              onChange={handlePickSaved}
              componentsProps={{
                popper: {
                  disablePortal: true,
                  sx: { zIndex: 10 },
                },
                paper: {
                  sx: { bgcolor: 'background.paper', backgroundImage: 'none' },
                },
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Pick existing location"
                  placeholder="Search by street, city, state, ZIP…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingOpts ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Typography variant="body2" color="text.secondary">
            Or enter a new address (address, city, state, ZIP required).
          </Typography>

          <AddressFields
            required
            showErrors={showErrors}
            showValidate={false}
            showCurrentLocation={false}
            helperText="Start typing for suggestions, or pick on the map."
            value={{
              address_line1: draft.location_address_line_1,
              address_line2: draft.location_address_line_2,
              city: draft.location_city,
              state: draft.location_state,
              zip_code: draft.location_zip,
              latitude: draft.location_latitude,
              longitude: draft.location_longitude,
            }}
            onChange={(next: AddressValue) =>
              onDraftChange(prev => ({
                ...prev,
                location_address_line_1: next.address_line1,
                location_address_line_2: next.address_line2,
                location_city: next.city,
                location_state: next.state,
                location_zip: next.zip_code,
                location_latitude: next.latitude ?? undefined,
                location_longitude: next.longitude ?? undefined,
              }))
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveNew} disabled={saving} sx={{ backgroundColor: accentColor }}>
          {saving ? <CircularProgress size={22} color="inherit" /> : 'Done'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransportationLocationDialog;
